import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import { 
  FileSpreadsheet, 
  Check,
  ArrowRight,
  Loader2,
  AlertCircle,
  Sparkles,
  Upload,
  Zap,
  Target,
  Phone,
  Mail,
  Users,
  Database,
  Lightbulb,
  Video,
  Scissors
} from 'lucide-react';
import SupportButton from '../components/SupportButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for use cases
const USE_CASE_ICONS = {
  'hubspot-phone-formatting': Phone,
  'crm-migration-cleanup': Database,
  'email-list-hygiene': Mail,
  'sales-lead-quality': Users,
  'phone-number-standardization': Phone,
  'webinar-lead-list-cleanup': Video,
};

// Relevant free tool mapping based on use case slug
const RELEVANT_TOOLS = {
  'phone-number-standardization': {
    name: 'Phone Formatter',
    description: 'Convert phone numbers to E.164 format instantly',
    link: '/tools/phone-formatter',
    icon: Phone,
    color: 'emerald'
  },
  'hubspot-phone-formatting': {
    name: 'Phone Formatter',
    description: 'Format phone numbers for HubSpot, Salesforce, and dialers',
    link: '/tools/phone-formatter',
    icon: Phone,
    color: 'emerald'
  },
  'webinar-lead-list-cleanup': {
    name: 'Name Splitter',
    description: 'Split "John Doe" into First Name + Last Name columns',
    link: '/tools/name-splitter',
    icon: Users,
    color: 'purple'
  },
  'email-list-hygiene': {
    name: 'Email Fixer',
    description: 'Fix typos and validate email formats',
    link: '/tools/email-formatter',
    icon: Mail,
    color: 'blue'
  },
  'crm-migration-cleanup': {
    name: 'File Splitter',
    description: 'Break large migration files into manageable chunks',
    link: '/tools/split',
    icon: Scissors,
    color: 'indigo'
  },
};

const UseCasePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [useCase, setUseCase] = useState(null);
  const [otherUseCases, setOtherUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUseCase();
    fetchOtherUseCases();
  }, [slug]);

  const fetchUseCase = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/use-cases/${slug}`);
      setUseCase(response.data.use_case);
      
      // Update page title for SEO
      if (response.data.use_case.meta_title) {
        document.title = response.data.use_case.meta_title;
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Use case not found');
      } else {
        setError('Failed to load page');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOtherUseCases = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/use-cases`);
      // Filter out the current use case
      const others = (response.data.use_cases || []).filter(uc => uc.slug !== slug);
      setOtherUseCases(others.slice(0, 3)); // Show max 3 other use cases
    } catch (err) {
      console.error('Failed to fetch other use cases:', err);
    }
  };

  // Get relevant free tool for this use case
  const relevantTool = RELEVANT_TOOLS[slug];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !useCase) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center py-20 pt-32">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{error || 'Page not found'}</h1>
            <p className="text-slate-600 mb-4">The page you&apos;re looking for doesn&apos;t exist.</p>
            <Button onClick={() => navigate('/')}>Go to Homepage</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Shared Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-indigo-50 to-white pt-24 pb-16 sm:pt-28 sm:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-6">
            <Target className="w-4 h-4" />
            Use Case
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-slate-900 mb-4">
            {useCase.title}
          </h1>
          
          {useCase.subtitle && (
            <p className="text-xl text-slate-600 mb-6">
              {useCase.subtitle}
            </p>
          )}
          
          {useCase.hero_description && (
            <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-8">
              {useCase.hero_description}
            </p>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
                <Upload className="w-5 h-5 mr-2" />
                Start Cleaning Free
              </Button>
            </Link>
            <Link to="/demo">
              <Button size="lg" variant="outline" className="px-8">
                <Sparkles className="w-5 h-5 mr-2" />
                See Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      {useCase.pain_points && useCase.pain_points.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-heading font-bold text-slate-900 text-center mb-8">
              Common Problems We Solve
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {useCase.pain_points.map((point, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-700">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {useCase.benefits && useCase.benefits.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-heading font-bold text-slate-900 text-center mb-8">
              How CRM Data Cleaner Helps
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {useCase.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                  <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-heading font-bold text-slate-900 text-center mb-8">
            How It Works
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-indigo-600">1</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Upload Your File</h3>
              <p className="text-sm text-slate-600">Drop your CSV or Excel file. We support files up to 500k rows.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-indigo-600">2</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Select Cleaning Rules</h3>
              <p className="text-sm text-slate-600">Choose what to clean: emails, phones, names, addresses, and more.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-indigo-600">3</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Download Clean Data</h3>
              <p className="text-sm text-slate-600">Get your cleaned file ready for CRM import in seconds.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Relevant Free Tool / Prepare for Migration */}
      {relevantTool && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-heading font-bold text-slate-900 text-center mb-6">
              {slug === 'crm-migration-cleanup' ? 'Prepare Data for Migration' : 'Try Our Free Tool'}
            </h2>
            <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-all">
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  relevantTool.color === 'emerald' ? 'bg-emerald-100' :
                  relevantTool.color === 'blue' ? 'bg-blue-100' :
                  relevantTool.color === 'purple' ? 'bg-purple-100' :
                  'bg-indigo-100'
                }`}>
                  <relevantTool.icon className={`w-7 h-7 ${
                    relevantTool.color === 'emerald' ? 'text-emerald-600' :
                    relevantTool.color === 'blue' ? 'text-blue-600' :
                    relevantTool.color === 'purple' ? 'text-purple-600' :
                    'text-indigo-600'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{relevantTool.name}</h3>
                  <p className="text-slate-600 mb-4">{relevantTool.description}</p>
                  <Link to={relevantTool.link}>
                    <Button className={`text-white ${
                      relevantTool.color === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' :
                      relevantTool.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                      relevantTool.color === 'purple' ? 'bg-purple-600 hover:bg-purple-700' :
                      'bg-indigo-600 hover:bg-indigo-700'
                    }`}>
                      Try {relevantTool.name} Free
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Use Cases */}
      {otherUseCases.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-heading font-bold text-slate-900 text-center mb-8">
              Explore More Use Cases
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {otherUseCases.map((uc) => {
                const IconComponent = USE_CASE_ICONS[uc.slug] || Lightbulb;
                return (
                  <Link
                    key={uc.use_case_id}
                    to={`/use-case/${uc.slug}`}
                    className="group bg-slate-50 rounded-xl border border-slate-200 p-5 hover:border-indigo-200 hover:shadow-md transition-all"
                  >
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-teal-600 transition-colors">
                      <IconComponent className="w-5 h-5 text-teal-600 group-hover:text-white" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-600 transition-colors">
                      {uc.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-2">
                      {uc.subtitle || 'Learn how to use CRM Data Cleaner for this use case.'}
                    </p>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-8">
              <Link to="/resources/use-cases">
                <Button variant="outline" className="rounded-full">
                  View All Use Cases
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center">
                <FileSpreadsheet className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-white">CRM Data Cleaner</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/pricing/subscription" className="hover:text-white">Pricing</Link>
              <Link to="/blog" className="hover:text-white">Blog</Link>
              <Link to="/resources/use-cases" className="hover:text-white">Use Cases</Link>
            </div>
          </div>
        </div>
      </footer>
      
      <SupportButton />
    </div>
  );
};

export default UseCasePage;
